import React, { useState, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useStripe } from '@stripe/react-stripe-js'
import qs from 'query-string'
import { ThemeProvider } from '@material-ui/core/styles'
import themes, { extendTheme } from 'themes'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import CircularProgress from '@material-ui/core/CircularProgress'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import { useGetProductByPaymentIntentId, GetStudentQuery } from './query'
import { useStyles } from './styles'
import { useSnackbar } from 'notistack'
import { withStripeElements } from 'lib/stripe'
import { useQuery } from '@apollo/client'
import Error from 'components/error'
import Disclaimer from 'components/disclaimer'
import LessonPackPurchaseConfirmation from './layouts/lesson-pack-purchase-confirmation'
import PracticeSessionPurchaseConfirmation from './layouts/practice-session-purchase-confirmation'
import EnrollmentPurchaseConfirmation from './layouts/enrollment-purchase-confirmation'
import PrivateLessonPurchaseConfirmation from './layouts/private-lesson-purchase-confirmation'
import PrivateLessonPurchaseReturn from './layouts/private-lesson-redesign/purchase-return'
import { PRIVATE_LESSON_PRODUCT_BY_PAYMENT_INTENT_ID } from './layouts/private-lesson-redesign/query'
import { amplitudeEvent } from 'lib/amplitude'
import {
  useFeatureFlags,
  useFeatureFlagsLoading,
  flags,
} from 'lib/feature-flags'
import { groupEventRegistrationEventProps } from 'pages/things-to-do/events/register/registration-experiment'
import { getInitialPurchasePage, getPurchaseTypeByPathname } from './utils'
import RouterLinkButton from 'components/router-link-button'
import VirtualCoachingPurchaseConfirmed from 'pages/virtual-coaching-student/purchase-confirmed'
import VirtualCoachingPurchaseFallback from 'pages/virtual-coaching-student/purchase-fallback'
import {
  PurchaseFailed as VirtualCoachingPurchaseFailed,
  PurchasePending as VirtualCoachingPurchasePending,
} from 'pages/virtual-coaching-student/purchase-return-status'
import { VIRTUAL_COACHING_PRODUCT_BY_PAYMENT_INTENT_ID } from 'pages/virtual-coaching-student/queries'
import {
  getPaymentIntentIdFromSecret,
  getReturnState,
  isRetryableLookupError,
  LOOKUP_MAX_RETRIES,
  LOOKUP_RETRY_DELAY_MS,
  PENDING_TIMEOUT_MS,
  RETURN_STATE,
} from 'pages/virtual-coaching-student/return-state'

const ProductPurchasePage = () => {
  const location = useLocation()
  const { pathname } = location
  const {
    payment_intent_client_secret: paymentIntentClientSecret,
    payment_intent: returnedPaymentIntentId,
    redirect_status: redirectStatus,
    lessonTypeId: returnedLessonTypeId,
  } = qs.parse(location.search)
  const stripe = useStripe()
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [isRedesignEnabled, isPrivateBookingRedesignEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_GROUP_EVENT_REGISTRATION_REDESIGN,
    flags.FLAG_FEAT_PRIVATE_BOOKING_REDESIGN,
  ])
  // Flags are evaluated anonymously first, then again after sign-in. Wait for
  // the signed-in value so flag-on users do not see the old page flash.
  const flagsLoading = useFeatureFlagsLoading()
  const [paymentIntentId, setPaymentIntentId] = useState(null)
  const [purchaseStatus, setPurchaseStatus] = useState('pending')
  const [pendingTimedOut, setPendingTimedOut] = useState(false)
  const { studentId, code } = useParams()
  const purchaseType = getPurchaseTypeByPathname(pathname)
  const isGroupEventPurchase = purchaseType === 'group-event'
  const isVirtualCoachingPurchase = purchaseType === 'virtual-coaching'
  const isPrivateLessonRoute = purchaseType === 'private-lesson'
  const isPrivateLessonRedesign =
    isPrivateLessonRoute && !!isPrivateBookingRedesignEnabled
  // Virtual coaching: not being able to verify the payment with Stripe must
  // not read as a failed payment (the order may exist), so it gets its own
  // status and the lookup decides what to show.
  const verifyFailedStatus = isVirtualCoachingPurchase ? 'unverified' : 'failed'

  useEffect(() => {
    let isMounted = true
    const pollInterval = 500 // 0.5 seconds
    const maxDuration = 60000 // 1 minute
    const startTime = Date.now()

    const pollPaymentIntent = async () => {
      if (!stripe || !paymentIntentClientSecret) return

      // Stop polling if 1 minute has passed
      if (Date.now() - startTime > maxDuration) {
        if (isMounted) {
          setPurchaseStatus(verifyFailedStatus)
          enqueueSnackbar('Payment verification timed out.', {
            variant: 'warning',
          })
        }
        return
      }

      try {
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(
          paymentIntentClientSecret,
        )

        if (!isMounted) return

        if (error) {
          setPurchaseStatus(verifyFailedStatus)
          enqueueSnackbar('Unable to verify payment status', {
            variant: 'error',
          })
          return
        }

        if (paymentIntent) {
          setPaymentIntentId(paymentIntent.id)
          switch (paymentIntent.status) {
            case 'requires_capture':
              setPurchaseStatus('pending')
              break
            case 'succeeded':
              setPurchaseStatus('complete')
              enqueueSnackbar('Payment succeeded!', { variant: 'success' })
              return
            case 'processing':
              setPurchaseStatus('processing')
              break
            case 'canceled':
              setPurchaseStatus('canceled')
              return
            default:
              break
          }
          setTimeout(pollPaymentIntent, pollInterval)
        }
      } catch (err) {
        if (isMounted) {
          setPurchaseStatus(verifyFailedStatus)
          enqueueSnackbar('Error verifying payment', { variant: 'error' })
        }
      }
    }

    pollPaymentIntent()

    return () => {
      isMounted = false
    }
  }, [stripe, paymentIntentClientSecret, enqueueSnackbar, verifyFailedStatus])

  // Backstop so the virtual coaching page is never pending forever (Stripe.js
  // that does not load, a lookup that never settles).
  useEffect(() => {
    if (!isVirtualCoachingPurchase) return undefined
    const timer = setTimeout(() => setPendingTimedOut(true), PENDING_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isVirtualCoachingPurchase])

  // The `payment_intent` Stripe puts in the return URL (or the id inside the
  // client secret) lets the virtual coaching lookup start without waiting for
  // Stripe.js to retrieve the intent.
  const virtualCoachingPaymentIntentId =
    returnedPaymentIntentId ||
    getPaymentIntentIdFromSecret(paymentIntentClientSecret)

  const { product, error, loading } = useGetProductByPaymentIntentId(
    isVirtualCoachingPurchase
      ? paymentIntentId || virtualCoachingPaymentIntentId || null
      : isPrivateLessonRoute && flagsLoading
        ? null
        : paymentIntentId,
    {
      includeEnrollmentTransactions: !isGroupEventPurchase,
      ...(isVirtualCoachingPurchase
        ? {
          query: VIRTUAL_COACHING_PRODUCT_BY_PAYMENT_INTENT_ID,
          maxRetries: LOOKUP_MAX_RETRIES,
          retryDelay: LOOKUP_RETRY_DELAY_MS,
          isRetryableError: isRetryableLookupError,
        }
        : {}),
      // The redesign needs the coach's cancellation fields, so it has its own
      // document and the shared query stays as it was.
      ...(isPrivateLessonRedesign
        ? { query: PRIVATE_LESSON_PRODUCT_BY_PAYMENT_INTENT_ID }
        : {}),
    },
  )

  // Only confirm once the payment settled; the request can exist earlier.
  const virtualCoachingRequest =
    product && isVirtualCoachingPurchase && purchaseStatus === 'complete'
      ? product
      : null

  const lessonPack = product && purchaseType === 'lesson-pack' ? product : null

  const practiceSession =
    product && purchaseType === 'practice-session' ? product : null

  const enrollment =
    product &&
    (purchaseType === 'group-event' || purchaseType === 'private-lesson')
      ? product
      : null

  const isPrivateLessonPurchase = product && purchaseType === 'private-lesson'

  const {
    data: fetchedStudent,
    error: studentError,
    loading: studentLoading,
  } = useQuery(GetStudentQuery, {
    variables: {
      studentId: studentId,
    },
    skip: !studentId || isPrivateLessonPurchase || isPrivateLessonRedesign,
  })

  useEffect(() => {
    if (purchaseStatus === 'complete' && product) {
      const amplitudePurchaseType =
        purchaseType === 'lesson-pack'
          ? 'lesson_pack'
          : purchaseType === 'practice-session'
            ? 'private_booking'
            : purchaseType === 'group-event'
              ? 'group_event'
              : purchaseType === 'private-lesson'
                ? 'private_lesson'
                : purchaseType === 'virtual-coaching'
                  ? 'virtual_coaching'
                  : null

      if (amplitudePurchaseType) {
        amplitudeEvent('complete-checkout', {
          purchase_type: amplitudePurchaseType,
          ...(amplitudePurchaseType === 'group_event'
            ? groupEventRegistrationEventProps()
            : {}),
          // guest private lesson purchases have no student in the route
          ...(amplitudePurchaseType === 'private_lesson'
            ? { is_guest: !studentId }
            : {}),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseStatus, product, purchaseType])

  const heading =
    purchaseStatus === 'canceled' ? 'Purchase Not Completed' : 'Thank you!'

  const getDisclaimerDescription = ({ purchaseStatus, pathname }) => {
    const purchaseType = getPurchaseTypeByPathname(pathname)
    const isGroupEventPurchase = purchaseType === 'group-event'

    const canceledPurchaseMessage = isGroupEventPurchase
      ? 'Unable to complete your purchase. The group event selected may be full.'
      : 'Unable to complete your purchase. The selected time slot may no longer be available. Please try again or select a different time.'

    if (purchaseStatus === 'canceled') {
      return canceledPurchaseMessage
    } else {
      return 'There was an issue loading your purchase details. Please try reloading the page.'
    }
  }

  const disclaimerAction =
    purchaseStatus === 'canceled' ? (
      <RouterLinkButton
        variant="contained"
        color="primary"
        to={getInitialPurchasePage(pathname, code, returnedLessonTypeId)}
      >
        Retry
      </RouterLinkButton>
    ) : (
      <Button
        variant="contained"
        color="primary"
        onClick={() => window.location.reload()}
      >
        Reload Page
      </Button>
    )

  if (isVirtualCoachingPurchase) {
    const returnState = getReturnState({
      product: virtualCoachingRequest,
      purchaseStatus,
      redirectStatus,
      hasClientSecret: Boolean(paymentIntentClientSecret),
      lookupError: error,
      timedOut: pendingTimedOut,
    })

    return (
      <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
        <Box display="flex" flexDirection="column" width="100%" mt={7} px={1}>
          {returnState === RETURN_STATE.CONFIRMED && (
            <VirtualCoachingPurchaseConfirmed
              request={virtualCoachingRequest}
            />
          )}
          {returnState === RETURN_STATE.FAILED && (
            <VirtualCoachingPurchaseFailed
              retryTo={getInitialPurchasePage(
                pathname,
                code,
                returnedLessonTypeId,
              )}
            />
          )}
          {returnState === RETURN_STATE.FALLBACK && (
            <VirtualCoachingPurchaseFallback purchaseStatus={purchaseStatus} />
          )}
          {returnState === RETURN_STATE.PENDING && (
            <VirtualCoachingPurchasePending />
          )}
        </Box>
      </ThemeProvider>
    )
  }

  // With the private booking redesign on, the confirmation (and its pending,
  // failed and fallback states) carries its own title, so the shared "Thank
  // you!" header, error disclaimer and legacy card are not rendered at all.
  if (isPrivateLessonRoute && flagsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isPrivateLessonRedesign) {
    return (
      <PrivateLessonPurchaseReturn
        purchaseStatus={purchaseStatus}
        enrollment={enrollment}
        error={error}
        retryTo={getInitialPurchasePage(pathname, code, returnedLessonTypeId)}
      />
    )
  }

  if (loading || studentLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  // With the registration redesign on, the group event confirmation card
  // carries its own "You're registered!" title, so once it is showing the
  // shared "Thank you!" header and divider would only repeat it. Every other
  // purchase type, the pending, processing and canceled states, and the flag-off
  // legacy card keep the header.
  const hideThankYouHeader =
    isRedesignEnabled &&
    isGroupEventPurchase &&
    purchaseStatus === 'complete' &&
    !!enrollment

  return (
    <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        mt={hideThankYouHeader ? 0 : 7}
        px={hideThankYouHeader ? 0 : 1}
      >
        {!hideThankYouHeader && (
          <Container className={classes.root} maxWidth="md">
            <Box>
              <Typography variant="h4">{heading}</Typography>
              <Typography variant="body1" className={classes.fontBolded}>
                {`Your purchase is ${purchaseStatus}.`}
                {purchaseStatus === 'complete' &&
                  ' You will receive a confirmation email shortly.'}
              </Typography>
            </Box>
            {(purchaseStatus === 'pending' ||
              purchaseStatus === 'processing') && <CircularProgress />}
            <Divider className={classes.divider} />
          </Container>
        )}
        {error && (
          <>
            <Box maxWidth="md" mx="auto" my={2}>
              <Disclaimer
                icon={<InfoOutlinedIcon style={{ fontSize: '32px' }} />}
                description={getDisclaimerDescription({
                  purchaseStatus,
                  pathname,
                })}
                action={disclaimerAction}
              />
            </Box>
            <Error
              error={error}
              message={error.message || 'Error loading product details'}
              showHeading={false}
            />
          </>
        )}
        {purchaseStatus === 'failed' && (
          <Error message="Payment verification failed." statusCode={500} />
        )}
        {lessonPack && (
          <LessonPackPurchaseConfirmation
            lessonPack={lessonPack}
            fetchedStudent={fetchedStudent}
            studentLoading={studentLoading}
            studentError={studentError}
          />
        )}
        {practiceSession && (
          <PracticeSessionPurchaseConfirmation
            practiceSession={practiceSession}
            fetchedStudent={fetchedStudent?.student}
          />
        )}
        {isGroupEventPurchase && enrollment && (
          <EnrollmentPurchaseConfirmation enrollment={enrollment} />
        )}
        {isPrivateLessonPurchase && enrollment && (
          <PrivateLessonPurchaseConfirmation lesson={enrollment} />
        )}
      </Box>
    </ThemeProvider>
  )
}

export default withStripeElements(ProductPurchasePage)
